// Console runtime-settings editor — the admin ⚙ for the Agent Console's live
// knobs: the two admission-lane timeouts, the per-turn wall-clock cap, and the
// concurrency limits. Loads GET /config on open and saves via PATCH /config.
//
// Timeouts + per-turn cap apply LIVE (the next turn reads the fresh config);
// the concurrency knobs apply on the next worker restart (the admission
// controller is process-wide). The form spells that out so an admin isn't
// surprised when a concurrency change doesn't take immediately.

import { useEffect, useState } from 'react';
import { Modal } from '../common/Modal';
import {
  getConfig,
  updateConfig,
  getModelConfig,
  updateModelConfig,
  getProfiles,
  type AgentConsoleConfig,
  type ModelConfigUpdate,
  type ModelOption,
} from '../../services/agentConsole';
import styles from './ConsoleSettingsPanel.module.css';

type FieldKey = keyof AgentConsoleConfig;

interface FieldDef {
  key: FieldKey;
  label: string;
  help: string;
  min: number;
  step: number;
}

// Two groups: live-effect timeouts, and restart-effect concurrency.
const TIMEOUT_FIELDS: FieldDef[] = [
  {
    key: 'admit_timeout_interactive_seconds',
    label: 'Interactive admit timeout',
    help: 'How long an interactive turn waits for a slot before failing fast (a human is waiting).',
    min: 0,
    step: 1,
  },
  {
    key: 'admit_timeout_background_seconds',
    label: 'Background admit timeout',
    help: 'How long a background turn waits for a slot before giving up.',
    min: 0,
    step: 5,
  },
  {
    key: 'turn_max_seconds',
    label: 'Per-turn cap',
    help: 'Hard wall-clock cap on a single turn, so a hung analysis can’t pin a slot forever.',
    min: 0,
    step: 30,
  },
];

const CONCURRENCY_FIELDS: FieldDef[] = [
  {
    key: 'max_concurrency',
    label: 'Max concurrency',
    help: 'Total simultaneous turns across the box (the admission semaphore).',
    min: 1,
    step: 1,
  },
  {
    key: 'reserved_interactive_slots',
    label: 'Reserved interactive slots',
    help: 'Slots held back for interactive turns so background work can’t starve them. Clamped below max concurrency.',
    min: 0,
    step: 1,
  },
];

function toForm(cfg: AgentConsoleConfig): Record<FieldKey, string> {
  return {
    admit_timeout_interactive_seconds: String(cfg.admit_timeout_interactive_seconds),
    admit_timeout_background_seconds: String(cfg.admit_timeout_background_seconds),
    turn_max_seconds: String(cfg.turn_max_seconds),
    max_concurrency: String(cfg.max_concurrency),
    reserved_interactive_slots: String(cfg.reserved_interactive_slots),
  };
}

// ── Per-agent default-model config (separate endpoint: /model-config) ────────
// One row per console agent; `loaded` is the value at fetch time so Save can send
// only genuinely-changed agents (an untouched agent stays inheriting the global).
interface AgentModelRow {
  id: string;
  name: string;
  value: string;
  loaded: string;
}

interface ModelForm {
  models: ModelOption[];
  defaultModel: string;
  agents: AgentModelRow[];
}

function ModelDropdown({
  models,
  value,
  onChange,
  disabled,
}: {
  models: ModelOption[];
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const known = models.some((m) => m.id === value);
  return (
    <select
      className={styles.input}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {!known && value ? <option value={value}>{value} (unavailable)</option> : null}
      {models.map((m) => (
        <option key={m.id} value={m.id} title={m.blurb}>
          {m.label} · {m.relative_cost}
        </option>
      ))}
    </select>
  );
}

function FieldRow({
  field,
  value,
  onChange,
  disabled,
}: {
  field: FieldDef;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{field.label}</span>
      <input
        type="number"
        className={styles.input}
        value={value}
        min={field.min}
        step={field.step}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className={styles.fieldHelp}>{field.help}</span>
    </label>
  );
}

export function ConsoleSettingsPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [form, setForm] = useState<Record<FieldKey, string> | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Per-agent default-model config (independent of the runtime knobs above).
  const [modelForm, setModelForm] = useState<ModelForm | null>(null);
  const [modelSaving, setModelSaving] = useState(false);
  const [modelSaved, setModelSaved] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaved(false);
    getConfig()
      .then((cfg) => {
        if (!cancelled) setForm(toForm(cfg));
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load config');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Load the per-agent default-model config + the agent list (one dropdown each).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setModelError(null);
    setModelSaved(false);
    Promise.all([getModelConfig(), getProfiles()])
      .then(([cfg, profs]) => {
        if (cancelled) return;
        setModelForm({
          models: cfg.models,
          defaultModel: cfg.default_model,
          agents: profs.profiles.map((p) => {
            const v = cfg.profile_overrides[p.id] ?? cfg.default_model;
            return { id: p.id, name: p.name, value: v, loaded: v };
          }),
        });
      })
      .catch((err) => {
        if (!cancelled)
          setModelError(err instanceof Error ? err.message : 'Failed to load models');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSaveModels = async () => {
    if (!modelForm) return;
    setModelSaving(true);
    setModelError(null);
    setModelSaved(false);
    // Send the global default + only the agents the admin actually changed (an
    // untouched agent stays inheriting the global; a changed one is pinned).
    const overrides: Record<string, string> = {};
    for (const a of modelForm.agents) {
      if (a.value !== a.loaded) overrides[a.id] = a.value;
    }
    const patch: ModelConfigUpdate = { default_model: modelForm.defaultModel };
    if (Object.keys(overrides).length > 0) patch.profile_overrides = overrides;
    try {
      const updated = await updateModelConfig(patch);
      setModelForm((f) =>
        f
          ? {
              models: updated.models,
              defaultModel: updated.default_model,
              agents: f.agents.map((a) => {
                const v = updated.profile_overrides[a.id] ?? updated.default_model;
                return { ...a, value: v, loaded: v };
              }),
            }
          : f,
      );
      setModelSaved(true);
    } catch (err) {
      setModelError(err instanceof Error ? err.message : 'Failed to save models');
    } finally {
      setModelSaving(false);
    }
  };

  const setField = (key: FieldKey, v: string) => {
    setForm((f) => (f ? { ...f, [key]: v } : f));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!form) return;
    // Parse every field; reject non-numbers before hitting the API so the error
    // is local and specific rather than a generic 422.
    const patch: Partial<AgentConsoleConfig> = {};
    for (const key of Object.keys(form) as FieldKey[]) {
      const n = Number(form[key]);
      if (form[key].trim() === '' || Number.isNaN(n)) {
        setError(`"${key}" must be a number.`);
        return;
      }
      patch[key] = n;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateConfig(patch);
      setForm(toForm(updated));
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Console settings" size="md">
      <p className={styles.panelIntro}>
        Runtime knobs for the shared agent box. The admission controller keys off
        these live for timeouts; concurrency changes apply on the next restart.
      </p>

      <div className={styles.panelBody}>
        {loading && <p className={styles.statusLine}>Loading…</p>}
        {error && (
          <p className={`${styles.statusLine} ${styles.statusError}`}>{error}</p>
        )}

        {form && (
          <>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Timeouts &amp; per-turn cap</h4>
                <span className={styles.liveBadge}>applies live</span>
              </div>
              {TIMEOUT_FIELDS.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  disabled={saving}
                />
              ))}
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4>Concurrency</h4>
                <span className={styles.restartBadge}>on restart</span>
              </div>
              {CONCURRENCY_FIELDS.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={form[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  disabled={saving}
                />
              ))}
            </section>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
              {saved && <span className={styles.savedNote}>Saved ✓</span>}
            </div>
          </>
        )}

        {modelError && !modelForm && (
          <p className={`${styles.statusLine} ${styles.statusError}`}>{modelError}</p>
        )}
        {modelForm && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h4>Default model per agent</h4>
              <span className={styles.liveBadge}>applies live</span>
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Global default</span>
              <ModelDropdown
                models={modelForm.models}
                value={modelForm.defaultModel}
                disabled={modelSaving}
                onChange={(v) => {
                  setModelForm((f) => (f ? { ...f, defaultModel: v } : f));
                  setModelSaved(false);
                }}
              />
              <span className={styles.fieldHelp}>
                Used when an agent has no specific default of its own.
              </span>
            </label>
            {modelForm.agents.map((a) => (
              <label key={a.id} className={styles.field}>
                <span className={styles.fieldLabel}>{a.name}</span>
                <ModelDropdown
                  models={modelForm.models}
                  value={a.value}
                  disabled={modelSaving}
                  onChange={(v) => {
                    setModelForm((f) =>
                      f
                        ? {
                            ...f,
                            agents: f.agents.map((x) =>
                              x.id === a.id ? { ...x, value: v } : x,
                            ),
                          }
                        : f,
                    );
                    setModelSaved(false);
                  }}
                />
                <span className={styles.fieldHelp}>
                  {a.value === modelForm.defaultModel
                    ? 'Using the global default.'
                    : 'Custom default for this agent.'}
                </span>
              </label>
            ))}
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSaveModels}
                disabled={modelSaving}
              >
                {modelSaving ? 'Saving…' : 'Save models'}
              </button>
              {modelSaved && <span className={styles.savedNote}>Saved ✓</span>}
              {modelError && (
                <span className={`${styles.statusLine} ${styles.statusError}`}>
                  {modelError}
                </span>
              )}
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
